import { AdUnitImportValidationResult, AdUnitsImportValidationResults } from '@pubstack/common/src/adunit'
import { FunctionComponent, useMemo } from 'react'
import { Colors } from '~/assets/style/colors'
import { EditPageWrapper } from '~/components/_styled/page'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import { ContentCard } from '~/components/ContentCard'
import { BaseFileTransfer } from '~/components/FileTransfer'

/** The H1 scale and the breadcrumb's own spacing reach into children the page does not render itself. */
const TITLE_BAR_CLASSES = 'flex w-full justify-between pb-10 [&_h1]:font-rubik [&_h1]:text-[20px]/[30px] [&_a]:mb-3'

const CONTENT_CARD_CLASSES = 'w-[1000px]'

const CARD_TITLE_CLASSES = 'm-0 inline-flex flex-1 items-center font-rubik text-[20px]/[30px] font-normal text-jet'

const SEPARATOR_CLASSES = 'my-5 border-t border-platinum'

const AD_UNITS_RESULTS_SECTION_CLASSES = 'mb-4'

const AD_UNITS_RESULTS_SECTION_TITLE_CLASSES = 'my-2'

const SUCCESSFUL_AD_UNITS_CLASSES = 'flex flex-wrap gap-2'

const ERRORED_AD_UNIT_CLASSES = 'mb-2 flex w-full gap-4'

const ACTION_BUTTONS_CLASSES = 'flex justify-end gap-4'

type Props = {
  breadcrumbs: React.ReactNode
  filename: string | undefined
  validationResults?: AdUnitsImportValidationResults
  isDownloading: boolean
  isValidating: boolean
  isCreating: boolean
  onBackClick: () => void
  onChange: (file: File | null) => void
  onValidate: () => void
  onDownload: () => void
  onDelete: () => void
  onCreate: () => void
}

export const PureAdmAdUnitImportPage: FunctionComponent<Props> = ({
  breadcrumbs,
  filename,
  validationResults,
  isDownloading,
  isValidating,
  isCreating,
  onBackClick,
  onChange,
  onValidate,
  onDownload,
  onDelete,
  onCreate,
}) => {
  // Ad units
  const successfulAdUnits: AdUnitImportValidationResult[] = useMemo(() => validationResults?.adUnits?.filter((e) => !e.errors) ?? [], [validationResults?.adUnits])
  const erroredAdUnits: AdUnitImportValidationResult[] = useMemo(() => validationResults?.adUnits?.filter((e) => e.errors) ?? [], [validationResults?.adUnits])

  return (
    <EditPageWrapper>
      <div className={TITLE_BAR_CLASSES}>
        {breadcrumbs}
        <Button variant="tertiary" onClick={onBackClick} trackingLabel={'adm-ad-units-import-back'}>
          Back
        </Button>
      </div>
      <ContentCard className={CONTENT_CARD_CLASSES} color={Colors.Turquoise}>
        <h2 className={CARD_TITLE_CLASSES}>Import ad units</h2>

        <div className={SEPARATOR_CLASSES} />

        <h2>Import file</h2>
        {/* Names the one file an owner can actually produce. The page used to point at the Admin, which was
            fine while it was staff-only and is a dead end now that owners reach it. */}
        <p>Upload a CSV of ad units. To get the right columns, use Export ad units on the ad units page and edit the file it gives you.</p>

        <div className={SEPARATOR_CLASSES} />

        <BaseFileTransfer
          label="Ad units"
          filename={filename}
          trackingPrefixAndLabel="adm-ad-units-import-file"
          isDownloading={isDownloading}
          onChange={(event) => onChange(event.target.files?.[0] ?? null)}
          onDownload={onDownload}
          onDelete={onDelete}
        >
          <Button
            className={'whitespace-nowrap'}
            variant="primary"
            iconName={isValidating ? 'pubstack' : undefined}
            iconSpin={isValidating}
            disabled={!!isValidating || !filename}
            trackingLabel={'adm-ad-units-import-file-verify'}
            onClick={onValidate}
          >
            {!isValidating ? 'Check file' : 'Checking file'}
          </Button>
        </BaseFileTransfer>

        <div className={SEPARATOR_CLASSES} />

        {validationResults && (
          <>
            <h2>Summary</h2>

            {/* Errors */}
            {validationResults?.errors && <p>{validationResults.errors.join(', ')}</p>}

            {/* Successful ad units */}
            {successfulAdUnits.length > 0 && (
              <div className={AD_UNITS_RESULTS_SECTION_CLASSES}>
                <div className={AD_UNITS_RESULTS_SECTION_TITLE_CLASSES}>{successfulAdUnits.length} new ad units</div>
                <div className={SUCCESSFUL_AD_UNITS_CLASSES}>
                  {successfulAdUnits.map((e) => (
                    <Chip key={e.line} text={e.name} color={Colors.Ghost} />
                  ))}
                </div>
              </div>
            )}

            {/* Errored ad units */}
            {erroredAdUnits.length > 0 && (
              <div className={AD_UNITS_RESULTS_SECTION_CLASSES}>
                <div className={AD_UNITS_RESULTS_SECTION_TITLE_CLASSES}>{erroredAdUnits.length} ad units with error will not be created</div>
                {erroredAdUnits.map((e) => (
                  <div className={ERRORED_AD_UNIT_CLASSES} key={e.line}>
                    {e.name && <Chip text={e.name} color={Colors.Mew} />}
                    <div>
                      line {e.line}: {e.errors?.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className={ACTION_BUTTONS_CLASSES}>
          <Button variant="tertiary" trackingLabel="adm-adunits-import-verify" onClick={onBackClick}>
            Cancel
          </Button>
          <Button
            variant="primary"
            iconSpin={isCreating}
            iconName={isCreating ? 'pubstack' : undefined}
            disabled={!validationResults || !!validationResults.errors?.length || !successfulAdUnits.length || isCreating}
            trackingLabel="adm-adunits-import-upload"
            onClick={onCreate}
          >
            Create ad units
          </Button>
        </div>
      </ContentCard>
    </EditPageWrapper>
  )
}
